import { useEffect, useRef, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Superscript from '@tiptap/extension-superscript';
import { ReferenceList, ReferenceItem } from './ReferenceListExtension';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CustomButton } from '@/components/uiWrappers/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { IoAddOutline } from 'react-icons/io5';
import { FaRegTrashCan } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';
import { PiCamera } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import {
  AddArticleInput,
  ArticleContent,
  ArticleContentSchema,
  ArticleData,
  ArticleImageUploadInput,
  ArticleStatus,
  ArticleAssignmentBucket,
  ARTICLE_IMAGE_EDITOR_MAX_BYTES,
  DeleteArticleImagesInput,
  ImageFileExtension,
  College,
} from '@mightybyte/business';
import { RichTextEditor } from './RichTextEditor';

interface ModuleAssignmentRow {
  moduleNumber: string;
  bucket: ArticleAssignmentBucket;
  order: string;
}

interface ArticleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pass existing article to switch to edit mode */
  article?: ArticleData;
}

const STATUS_LABELS: Record<ArticleStatus, string> = {
  [ArticleStatus.Draft]: 'Draft',
  [ArticleStatus.Published]: 'Published',
  [ArticleStatus.Hidden]: 'Hidden',
};

const BUCKET_LABELS: Record<ArticleAssignmentBucket, string> = {
  [ArticleAssignmentBucket.General]: 'General (all buckets)',
  [ArticleAssignmentBucket.One]: 'Bucket One',
  [ArticleAssignmentBucket.Two]: 'Bucket Two',
};

const COLLEGE_LABELS: Record<College, string> = {
  [College.LMU]: 'LMU',
  [College.Lehigh]: 'Lehigh',
};

const DEFAULT_TITLE_COLOR = '#ffffff';
const MAX_EDITOR_IMAGE_SIZE = ARTICLE_IMAGE_EDITOR_MAX_BYTES;
const EMPTY_EDITOR_CONTENT: ArticleContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

function resolveImageFileExtension(file: File): ImageFileExtension | null {
  switch (file.type) {
    case 'image/png':
      return ImageFileExtension.PNG;
    case 'image/jpeg':
      return ImageFileExtension.JPG;
    case 'image/webp':
      return ImageFileExtension.WEBP;
    default: {
      const loweredName = file.name.toLowerCase();
      if (loweredName.endsWith('.png')) return ImageFileExtension.PNG;
      if (loweredName.endsWith('.jpeg')) return ImageFileExtension.JPEG;
      if (loweredName.endsWith('.jpg')) return ImageFileExtension.JPG;
      if (loweredName.endsWith('.webp')) return ImageFileExtension.WEBP;
      return null;
    }
  }
}

function collectImageUrls(content?: ArticleContent): string[] {
  if (!content?.content) return [];

  const urls = new Set<string>();

  const visit = (node: ArticleContent['content'][number]) => {
    if (node.type === 'image') {
      const src = (node.attrs as { src?: unknown } | undefined)?.src;
      if (typeof src === 'string' && src.startsWith('http')) {
        urls.add(src);
      }
    }

    if (node.content?.length) {
      node.content.forEach(visit);
    }
  };

  content.content.forEach(visit);

  return [...urls];
}

const DEFAULT_ASSIGNMENT: ModuleAssignmentRow = { moduleNumber: '', bucket: ArticleAssignmentBucket.General, order: '0' };

function buildInitialAssignments(article?: ArticleData): ModuleAssignmentRow[] {
  if (!article || article.moduleAssignments.length === 0) return [{ ...DEFAULT_ASSIGNMENT }];
  return article.moduleAssignments.map((a) => ({
    moduleNumber: String(a.moduleNumber),
    bucket: a.bucket,
    order: String(a.order ?? 0),
  }));
}

function buildInitialEditorContent(article?: ArticleData): ArticleContent {
  return article?.content ?? EMPTY_EDITOR_CONTENT;
}

export function ArticleFormModal({ isOpen, onClose, article }: ArticleFormModalProps) {
  const isEdit = Boolean(article);
  const utils = trpc.useUtils();

  // Fetch full article with content when editing
  const { data: fullArticleData, isLoading: isLoadingFullArticle } = trpc.admin.article.getById.useQuery(
    { id: article?._id ?? '' },
    { enabled: isOpen && isEdit && Boolean(article?._id) }
  );

  // Use full article data if available, otherwise use the prop
  const articleWithContent = fullArticleData?.article ?? article;

  const [title, setTitle] = useState(articleWithContent?.title ?? '');
  const [imageUrl, setImageUrl] = useState(articleWithContent?.imageUrl ?? '');
  const [imagePreview, setImagePreview] = useState(articleWithContent?.imageUrl ?? '');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [titleColor, setTitleColor] = useState(articleWithContent?.titleColor ?? DEFAULT_TITLE_COLOR);
  const [shortDescription, setShortDescription] = useState(articleWithContent?.shortDescription ?? '');
  const [status, setStatus] = useState<ArticleStatus>(articleWithContent?.status ?? ArticleStatus.Draft);
  const [college, setCollege] = useState<College | undefined>(articleWithContent?.college ?? undefined);
  const [assignments, setAssignments] = useState<ModuleAssignmentRow[]>(
    buildInitialAssignments(articleWithContent),
  );
  const [error, setError] = useState('');
  const [draftArticleId, setDraftArticleId] = useState<string | null>(null);
  const [initialImageUrls, setInitialImageUrls] = useState<string[]>([]);
  const [sessionUploadedImageUrls, setSessionUploadedImageUrls] = useState<string[]>([]);
  const [isUploadingEditorImage, setIsUploadingEditorImage] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2],
        },
      }),
      Image,
      Link.configure({
        openOnClick: false,
        protocols: ['parentingfirst'],
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Write the article body here...',
        emptyEditorClass: 'is-editor-empty before:content-[attr(data-placeholder)] before:text-text-tertiary before:pointer-events-none before:float-left before:h-0',
      }),
      Superscript,
      ReferenceList,
      ReferenceItem,
    ],
    content: buildInitialEditorContent(article),
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
  });

  const getImageUploadUrlMutation = trpc.admin.quiz.getQuestionImageUploadUrl.useMutation();
  const getArticleEditorImageUploadUrlMutation = trpc.admin.article.getImageUploadUrl.useMutation();
  const deleteArticleImagesMutation = trpc.admin.article.deleteImages.useMutation();
  const deleteArticleMutation = trpc.admin.article.deleteArticle.useMutation();

  const invalidateArticles = () => utils.admin.article.getArticles.invalidate();

  const addMutation = trpc.admin.article.addArticle.useMutation();

  const updateMutation = trpc.admin.article.updateArticle.useMutation();

  const isPending =
    addMutation.isPending ||
    updateMutation.isPending ||
    getImageUploadUrlMutation.isPending ||
    getArticleEditorImageUploadUrlMutation.isPending ||
    deleteArticleImagesMutation.isPending ||
    deleteArticleMutation.isPending ||
    isClosing;

  useEffect(() => {
    if (!isOpen) return;

    setTitle(articleWithContent?.title ?? '');
    setImageUrl(articleWithContent?.imageUrl ?? '');
    setImagePreview(articleWithContent?.imageUrl ?? '');
    setTitleColor(articleWithContent?.titleColor ?? DEFAULT_TITLE_COLOR);
    setShortDescription(articleWithContent?.shortDescription ?? '');
    setStatus(articleWithContent?.status ?? ArticleStatus.Draft);
    setCollege(articleWithContent?.college ?? undefined);
    setAssignments(buildInitialAssignments(articleWithContent));
    setError('');
    setDraftArticleId(null);
    setSessionUploadedImageUrls([]);

    const initialContent = buildInitialEditorContent(articleWithContent);
    setInitialImageUrls(collectImageUrls(initialContent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, article?._id, article?.updatedAt, fullArticleData]);

  // Separate effect to update editor content when editor becomes available or article changes
  useEffect(() => {
    if (!isOpen || !editor) return;

    const initialContent = buildInitialEditorContent(articleWithContent);
    editor.commands.setContent(initialContent, {
      emitUpdate: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editor, articleWithContent?.content]);

  const deleteArticleImages = async (input: DeleteArticleImagesInput) => {
    if (input.imageUrls.length === 0) return;

    await deleteArticleImagesMutation.mutateAsync(input);
  };

  const ensureDraftArticleIdForEditor = async (): Promise<string> => {
    if (article?._id) return article._id;
    if (draftArticleId) return draftArticleId;

    const titleForDraft = title.trim() || 'Untitled Article';
    const response = await addMutation.mutateAsync({
      title: titleForDraft,
      status: ArticleStatus.Draft,
    });

    setDraftArticleId(response.article._id);
    return response.article._id;
  };

  const handleEditorImageUpload = async (file: File) => {
    if (!editor) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > MAX_EDITOR_IMAGE_SIZE) {
      toast.error('Image size must be less than 50MB');
      return;
    }

    const fileExtension = resolveImageFileExtension(file);
    if (!fileExtension) {
      toast.error('Unsupported image format. Use JPG, PNG, or WEBP.');
      return;
    }

    try {
      setIsUploadingEditorImage(true);

      const articleId = await ensureDraftArticleIdForEditor();
      const uploadInput: ArticleImageUploadInput = {
        articleId,
        fileExtension,
      };
      const uploadData =
        await getArticleEditorImageUploadUrlMutation.mutateAsync(uploadInput);

      const uploadResponse = await fetch(uploadData.signedUrl, {
        method: uploadData.requestMethod,
        headers: uploadData.headers as Record<string, string>,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      editor.chain().focus().setImage({ src: uploadData.publicUrl, alt: file.name }).run();
      setSessionUploadedImageUrls((prev) => (
        prev.includes(uploadData.publicUrl) ? prev : [...prev, uploadData.publicUrl]
      ));
    } catch (uploadError: any) {
      toast.error(uploadError?.message || 'Failed to upload image');
    } finally {
      setIsUploadingEditorImage(false);
    }
  };

  const cleanupUnsavedEditorResources = async () => {
    const effectiveArticleId = article?._id ?? draftArticleId;

    if (effectiveArticleId && sessionUploadedImageUrls.length > 0) {
      try {
        await deleteArticleImages({
          articleId: effectiveArticleId,
          imageUrls: sessionUploadedImageUrls,
        });
      } catch {
        // Best effort; do not block modal close if cleanup fails.
      }
    }

    if (!article?._id && draftArticleId) {
      try {
        await deleteArticleMutation.mutateAsync({ id: draftArticleId });
      } catch {
        // Best effort; orphan draft cleanup can happen later manually.
      }
    }
  };

  const handleClose = async (skipCleanup = false) => {
    try {
      setIsClosing(true);
      if (!skipCleanup) {
        await cleanupUnsavedEditorResources();
      }
    } finally {
      if (!isEdit) {
        setTitle('');
        setImageUrl('');
        setImagePreview('');
        setTitleColor(DEFAULT_TITLE_COLOR);
        setShortDescription('');
        setStatus(ArticleStatus.Draft);
        setCollege(undefined);
        setAssignments([{ ...DEFAULT_ASSIGNMENT }]);
        setDraftArticleId(null);
        setInitialImageUrls([]);
        setSessionUploadedImageUrls([]);
        editor?.commands.setContent(EMPTY_EDITOR_CONTENT, {
          emitUpdate: false,
        });
      }
      setError('');
      setIsClosing(false);
      onClose();
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > ARTICLE_IMAGE_EDITOR_MAX_BYTES) {
      toast.error('Image size must be less than 50MB');
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    await uploadImage(file);
  };

  const uploadImage = async (file: File) => {
    try {
      setIsUploadingImage(true);

      const fileExtension = resolveImageFileExtension(file);
      if (!fileExtension) {
        toast.error('Unsupported image format. Use JPG, PNG, or WEBP.');
        return;
      }

      const articleId = await ensureDraftArticleIdForEditor();
      const uploadData = await getArticleEditorImageUploadUrlMutation.mutateAsync({
        articleId,
        fileExtension,
      });

      const uploadResponse = await fetch(uploadData.signedUrl, {
        method: uploadData.requestMethod as string,
        headers: uploadData.headers as Record<string, string>,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      setImageUrl(uploadData.publicUrl);
      toast.success('Image uploaded successfully!');
    } catch {
      toast.error('Failed to upload image');
      setImageUrl('');
      setImagePreview('');
    } finally {
      setIsUploadingImage(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setImageUrl('');
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addAssignmentRow = () => {
    setAssignments((prev) => [
      ...prev,
      { moduleNumber: '', bucket: ArticleAssignmentBucket.General, order: '0' },
    ]);
  };

  const removeAssignmentRow = (index: number) => {
    setAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAssignmentRow = (
    index: number,
    field: keyof ModuleAssignmentRow,
    value: string,
  ) => {
    setAssignments((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const validate = (): boolean => {
    if (!title.trim()) {
      setError('Title is required');
      return false;
    }
    if (assignments.length === 0) {
      setError('Module assignment is required');
      return false;
    }
    for (let i = 0; i < assignments.length; i++) {
      const n = parseInt(assignments[i].moduleNumber, 10);
      if (isNaN(n) || n <= 0) {
        setError(`Assignment ${i + 1}: module number must be a positive integer`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const buildAssignments = () =>
    assignments
      .filter((a) => a.moduleNumber.trim() !== '')
      .map((a) => ({
        moduleNumber: parseInt(a.moduleNumber, 10),
        bucket: a.bucket,
        order: Math.max(0, parseInt(a.order, 10) || 0),
      }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!editor) {
      setError('Editor is still loading. Please wait a second and try again.');
      return;
    }

    const parsedContent = ArticleContentSchema.safeParse(editor.getJSON());
    if (!parsedContent.success) {
      setError('Article content contains unsupported formatting. Please adjust and try again.');
      return;
    }

    const payload: AddArticleInput = {
      title: title.trim(),
      imageUrl: imageUrl || undefined,
      titleColor,
      shortDescription: shortDescription.trim() || undefined,
      content: parsedContent.data,
      status,
      moduleAssignments: buildAssignments(),
      college,
    };

    try {
      let articleId = article?._id ?? draftArticleId;

      if (articleId) {
        await updateMutation.mutateAsync({ id: articleId, ...payload });
      } else {
        const createResponse = await addMutation.mutateAsync(payload);
        articleId = createResponse.article._id;
      }

      const currentImageUrls = collectImageUrls(parsedContent.data);
      const currentImageUrlSet = new Set(currentImageUrls);
      const trackedImageUrls = [
        ...new Set([...initialImageUrls, ...sessionUploadedImageUrls]),
      ];
      const removedImageUrls = trackedImageUrls.filter((url) => !currentImageUrlSet.has(url));

      if (articleId && removedImageUrls.length > 0) {
        try {
          await deleteArticleImages({
            articleId,
            imageUrls: removedImageUrls,
          });
        } catch {
          toast.error('Article saved, but cleanup of removed images failed.');
        }
      }

      await invalidateArticles();
      toast.success(isEdit || draftArticleId ? 'Article updated successfully!' : 'Article created successfully!');
      await handleClose(true);
    } catch (submitError: any) {
      toast.error(submitError?.message || 'Failed to save article');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isPending) void handleClose();
      }}
    >
      <DialogContent className="max-w-6xl sm:max-w-6xl w-full h-[95vh] flex flex-col p-0 gap-0 overflow-hidden" showCloseButton={true}>
        <DialogHeader className="px-6 py-4 border-b shrink-0 rounded-t-xl">
          <DialogTitle className="text-xl font-semibold">
            {isEdit ? 'Edit Article' : 'Create New Article'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <ScrollArea className="flex-1 overflow-auto">
            <div className="px-6">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] gap-12">

                {/* Left column — form fields */}
                <div className="space-y-4">

                  {/* Title */}
                  <div>
                    <Label htmlFor="articleTitle">Title *</Label>
                    <Input
                      id="articleTitle"
                      type="text"
                      value={title}
                      onChange={(e) => { setTitle(e.target.value); setError(''); }}
                      placeholder="Article title"
                      autoFocus
                    />
                  </div>

                  {/* Module Assignments */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Module Assignments</Label>
                      <button
                        type="button"
                        onClick={addAssignmentRow}
                        className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                      >
                        <IoAddOutline size={16} />
                        Add
                      </button>
                    </div>

                    {assignments.length === 0 && (
                      <p className="text-sm text-destructive italic">At least one module assignment is required.</p>
                    )}

                    <div className="flex flex-col gap-2">
                      {assignments.map((row, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={row.moduleNumber}
                            onChange={(e) => updateAssignmentRow(i, 'moduleNumber', e.target.value)}
                            placeholder="Module #"
                            className="w-24"
                          />
                          <Select
                            value={row.bucket}
                            onValueChange={(v) => updateAssignmentRow(i, 'bucket', v)}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(ArticleAssignmentBucket).map((b) => (
                                <SelectItem key={b} value={b}>
                                  {BUCKET_LABELS[b]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="0"
                            value={row.order}
                            onChange={(e) => updateAssignmentRow(i, 'order', e.target.value)}
                            placeholder="Order"
                            className="w-16"
                            title="Display order (lower = first)"
                          />
                          <button
                            type="button"
                            onClick={() => removeAssignmentRow(i)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <FaRegTrashCan className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cover Image Upload */}
                  <div>
                    <Label>Cover Image (optional)</Label>
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Cover preview"
                          className="w-full h-48 object-cover rounded-lg border-2 border-dashed border-border bg-muted"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-2 hover:bg-destructive/90 shadow-md"
                          disabled={isUploadingImage}
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                        {isUploadingImage && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                            <div className="text-white text-sm font-medium">Uploading...</div>
                          </div>
                        )}
                        {title && (
                          <>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent rounded-lg pointer-events-none" />
                            <p
                              className="absolute bottom-3 left-3 right-3 text-sm font-semibold leading-tight pointer-events-none line-clamp-2"
                              style={{ color: titleColor }}
                            >
                              {title}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          id="articleCoverImage"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          disabled={isUploadingImage}
                        />
                        <Label
                          htmlFor="articleCoverImage"
                          className={cn(
                            'flex flex-col items-center justify-center w-full h-48',
                            'border-2 border-dashed border-border rounded-lg',
                            'cursor-pointer hover:bg-muted transition-colors bg-background',
                            isUploadingImage && 'opacity-50 cursor-not-allowed',
                          )}
                        >
                          <PiCamera className="w-16 h-16 text-muted-foreground mb-3" />
                          <span className="text-base text-muted-foreground font-medium">
                            {isUploadingImage ? 'Uploading...' : 'Upload Cover Image'}
                          </span>
                          <span className="text-xs text-muted-foreground/70 mt-2">Max 50MB, JPG/PNG</span>
                        </Label>
                      </div>
                    )}
                  </div>

                  {/* Title Color */}
                  <div>
                    <Label htmlFor="articleTitleColor">Title Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        id="articleTitleColor"
                        type="color"
                        value={titleColor}
                        onChange={(e) => setTitleColor(e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border border-border bg-transparent p-0.5"
                      />
                      <span className="text-sm text-muted-foreground font-mono">{titleColor}</span>
                      {titleColor !== DEFAULT_TITLE_COLOR && (
                        <button
                          type="button"
                          onClick={() => setTitleColor(DEFAULT_TITLE_COLOR)}
                          className="text-xs text-primary hover:underline"
                        >
                          Reset to white
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Short Description */}
                  <div>
                    <Label htmlFor="articleDescription">Short Description (optional)</Label>
                    <Input
                      id="articleDescription"
                      type="text"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                      placeholder="Brief summary of the article"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <Label>Status</Label>
                    <Select value={status} onValueChange={(v) => setStatus(v as ArticleStatus)}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(ArticleStatus).map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Institution Filter */}
                  <div>
                    <Label>Institution (optional)</Label>
                    <Select
                      value={college ?? 'all'}
                      onValueChange={(v) => setCollege(v === 'all' ? undefined : v as College)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="All institutions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Institutions</SelectItem>
                        {Object.values(College).map((c) => (
                          <SelectItem key={c} value={c}>
                            {COLLEGE_LABELS[c]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      If set, article will only show to users from this institution
                    </p>
                  </div>

                  {error && <p className="text-sm text-destructive">{error}</p>}
                </div>

                {/* Right column — article body */}
                <div className="space-y-2 sticky top-0 self-start">
                  <Label>Article Content</Label>
                  <RichTextEditor
                    editor={editor}
                    onImageSelected={handleEditorImageUpload}
                    isUploadingImage={isUploadingEditorImage}
                    className="h-[calc(95vh-160px)]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Supported formatting: headings, bold, italic, strike, links, lists, quotes, code blocks, and images.
                  </p>
                </div>

              </div>
            </div>
          </ScrollArea>

          <div className="px-6 py-4 border-t shrink-0 bg-background rounded-b-xl flex gap-3 justify-end">
            <CustomButton
              type="button"
              variant="outline"
              onClick={() => { void handleClose(); }}
              disabled={isPending}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="default"
              disabled={isPending || isUploadingImage || isUploadingEditorImage || !editor}
            >
              {isPending
                ? 'Saving...'
                : isEdit || draftArticleId ? 'Save Changes' : 'Create Article'}
            </CustomButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
